/**
 * @fileoverview Команды диспетчера: воздух, гейты, уборка.
 */

import { Snd } from "../audio/sound";
import { HOLD_SHORT } from "../constants/world";
import { pathTo } from "../plane/path";
import type {
  CmdCheck,
  GameHooks,
  GameState,
  Plane,
  Runway,
} from "../state/types";
import { clamp, dist, rnd } from "../util/math";
import { killHazardsFor, startHazard } from "../systems/hazards";
import { addScore, breakStreak } from "../systems/scoring";

/** Можно ли посадить борт на полосу. */
export function canLand(p: Plane, r: Runway): CmdCheck {
  if (p.status !== "approach") return { ok: false };
  if (r.plane) return { ok: false, why: "полоса занята" };
  if (r.reservedFor && p.id !== r.reservedFor) {
    return { ok: false, why: `резерв под ${r.reserveLabel || "медрейс"}` };
  }
  const risky = r.closed || r.snow > 62 || (r.ice || 0) > 40;
  return {
    ok: true,
    risky,
    why: r.closed
      ? "полоса закрыта!"
      : r.snow > 62
        ? `занос ${Math.round(r.snow)}%`
        : (r.ice || 0) > 40
          ? `лед ${Math.round(r.ice)}%`
          : "",
  };
}

/** Команда посадки на полосу `ri`. */
export function cmdLand(
  g: GameState,
  hooks: GameHooks,
  p: Plane,
  ri: number,
  opts?: { force?: boolean },
): void {
  const r = g.runways[ri];
  if (!r) return;
  const c = canLand(p, r);
  if (!c.ok) return;

  const go = (): void => {
    r.plane = p;
    p.status = "inbound";
    p.rwy = ri;
    p.joining = false;
    const fafX = Math.max(120, r.x1 - 40);
    const midX = clamp(p.x, 160, 1400);
    const midY = Math.min(p.y, r.y - 70);
    const thr = { x: r.x1 + 90, y: r.y };
    p.path = [];
    if (dist(p.x, p.y, midX, midY) > 50) p.path.push({ x: midX, y: midY });
    p.path.push({ x: fafX, y: r.y - 16 });
    p.path.push(thr);
    p.landTotal = 0;
    let px = p.x;
    let py = p.y;
    for (const w of p.path) {
      p.landTotal += dist(px, py, w.x, w.y);
      px = w.x;
      py = w.y;
    }
    p.landLeft = p.landTotal;
    Snd.ok();
    hooks.fx(p.x, p.y, `ЗАХОД ${r.name}`, "#5fd7ff");
    if (r.closed) startHazard(g, hooks, "closed", p, r, 6.5);
    if (c.risky) g.riskyOps++;
    if (g.tutStep === 1 && p.tut) hooks.tutNext();
  };

  if (c.risky && !opts?.force) {
    hooks.askConfirm(
      `Рискованная посадка на ${r.name}${c.why ? `: ${c.why}` : ""}. Подтвердить?`,
      go,
    );
  } else go();
}

/** Уход на второй круг. */
export function cmdGoAround(
  g: GameState,
  hooks: GameHooks,
  p: Plane,
): void {
  if (
    p.status !== "approach" &&
    p.status !== "inbound" &&
    p.status !== "landing"
  ) {
    return;
  }
  if (p.rwy !== null && g.runways[p.rwy]?.plane === p) {
    g.runways[p.rwy]!.plane = null;
  }
  killHazardsFor(g, p);
  p.rwy = null;
  p.status = "goaround";
  p.gaT = 0;
  p.path = [{ x: p.x + (p.x < 800 ? -1 : 1) * 520, y: -140 }];
  p.pat = Math.max(0.28, p.pat - 0.22);
  g.goarounds++;
  addScore(g, hooks, -75, p, "УХОД НА КРУГ");
  breakStreak(g, hooks);
  Snd.bad();
  hooks.toast(`${p.cs} уходит на второй круг`, "bad");
}

/** Эшелон ожидания (экономия топлива). */
export function cmdHold(g: GameState, hooks: GameHooks, p: Plane): void {
  void g;
  if (p.status !== "approach" || p.holdBonus > 0) return;
  p.holdBonus = 14;
  Snd.click();
  hooks.fx(p.x, p.y, "ЭШЕЛОН ОЖИДАНИЯ", "#b58cff");
  hooks.toast(`${p.cs} переведен в зону ожидания — топливо экономится`);
}

/** Можно ли назначить гейт. */
export function canGate(p: Plane): boolean {
  return p.status === "awaiting_gate" || p.status === "vacating";
}

/** Назначить гейт. */
export function cmdGate(
  g: GameState,
  hooks: GameHooks,
  p: Plane,
  gi: number,
): void {
  const gate = g.gates[gi];
  if (!gate || !canGate(p) || (gate.plane && !gate.plane.dead && gate.plane !== p))
    return;

  if (p.gate !== null && p.gate !== gi && g.gates[p.gate]?.plane === p) {
    g.gates[p.gate]!.plane = null;
  }
  if (
    p.pendingGate !== null &&
    p.pendingGate !== undefined &&
    p.pendingGate !== gi
  ) {
    const og = g.gates[p.pendingGate];
    if (og && og.plane === p) og.plane = null;
  }

  gate.plane = p;
  p.gate = gi;
  p.apron = null;
  if (p.status === "vacating") {
    p.pendingGate = gi;
  } else {
    p.pendingGate = null;
    p.status = "taxi_gate";
    p.path = pathTo(p, { x: gate.x, y: gate.y }, true);
  }
  const askT = p.gateAskT || g.elapsed;
  const fast = g.elapsed - askT < 8;
  addScore(
    g,
    hooks,
    fast ? 60 : 25,
    p,
    fast ? "ГЕЙТ ЗА <8 С" : `ГЕЙТ ${gate.name}`,
  );
  Snd.ok();
  if (g.tutStep === 2 && p.tut) hooks.tutNext();
}

/** Руление к hold-short выбранной полосы. */
export function cmdTaxiOut(
  g: GameState,
  hooks: GameHooks,
  p: Plane,
  ri: number,
): void {
  if (p.status !== "ready") return;
  if (p.gate !== null) g.gates[p.gate]!.plane = null;
  p.status = "taxi_rwy";
  p.rwyWish = ri;
  p.gate = null;
  const hs = HOLD_SHORT[ri];
  if (!hs) return;
  p.path = pathTo(p, hs, true);
  Snd.ok();
  hooks.fx(p.x, p.y, "РУЛЕНИЕ", "#7ee08a");
}

/** Можно ли разрешить взлет. */
export function canTakeoff(g: GameState, p: Plane): CmdCheck {
  if (p.status !== "hold_short") return { ok: false };
  const wish = p.rwyWish;
  if (wish === null || wish === undefined) return { ok: false };
  const r = g.runways[wish];
  if (!r) return { ok: false };
  if (r.plane) {
    const occ = r.plane;
    if (
      !occ.dead &&
      occ.status === "vacating" &&
      Math.abs(occ.y - r.y) > 28
    ) {
      r.plane = null;
    } else if (r.plane) {
      return {
        ok: false,
        why:
          !occ.dead && occ.status === "vacating"
            ? "борт сходит с полосы"
            : "полоса занята",
      };
    }
  }
  if (r.reservedFor && p.id !== r.reservedFor) {
    return { ok: false, why: `резерв под ${r.reserveLabel || "медрейс"}` };
  }
  if ((g.takeoffLockT || 0) > 0 && !p.medevac) {
    return {
      ok: false,
      why: `взлеты ограничены · ${Math.ceil(g.takeoffLockT)}с`,
    };
  }
  if (p.needsDeice && !p.deiced && !p.deiceWaive) {
    return { ok: false, why: "нужна обработка от обледенения" };
  }
  const risky = r.closed || r.snow > 72 || (r.ice || 0) > 45;
  return {
    ok: true,
    risky,
    why: r.closed
      ? "техника на полосе"
      : r.snow > 72
        ? `занос ${Math.round(r.snow)}%`
        : (r.ice || 0) > 45
          ? "лед"
          : "",
  };
}

/** Разрешить взлет. */
export function cmdTakeoff(
  g: GameState,
  hooks: GameHooks,
  p: Plane,
): void {
  const c = canTakeoff(g, p);
  if (!c.ok) {
    if (p.needsDeice && !p.deiced && !p.deiceWaive) {
      hooks.toast(
        `${p.cs} нельзя выпускать без обработки от обледенения`,
        "bad",
      );
      g.delays++;
      g.paxMood -= 5;
    } else if (c.why) {
      hooks.toast(`${p.cs}: взлет нельзя — ${c.why}`, "bad");
    }
    return;
  }

  const go = (): void => {
    const wish = p.rwyWish!;
    const r = g.runways[wish]!;
    const hs = HOLD_SHORT[wish]!;
    r.plane = p;
    p.rwy = wish;
    p.status = "takeoff";
    p.v = 0;
    p.path = [
      { x: hs.x, y: r.y },
      { x: r.x1 + 90, y: r.y },
    ];
    p.rollStarted = false;
    Snd.up();
    hooks.fx(p.x, p.y, "ВЗЛЕТ РАЗРЕШЕН", "#7ee08a");
    if (r.closed) startHazard(g, hooks, "closed", p, r, 6);
    if (c.risky) g.riskyOps++;
  };

  if (c.risky) {
    const r = g.runways[p.rwyWish!]!;
    hooks.askConfirm(
      `Рискованный взлет с ${r.name}${c.why ? `: ${c.why}` : ""}. Подтвердить?`,
      go,
    );
  } else go();
}

/** Начать уборку снега на полосе. */
export function cmdClearSnow(
  g: GameState,
  hooks: GameHooks,
  ri: number,
): void {
  const r = g.runways[ri];
  if (!r) return;
  if (r.plane) {
    const occ = r.plane;
    const vacating =
      !occ.dead &&
      occ.status === "vacating" &&
      Math.abs(occ.y - r.y) > 28;
    if (vacating) {
      // Борт уже ушел с оси — снимаем занятость и даем вывести технику
      r.plane = null;
    } else {
      const why =
        !occ.dead && occ.status === "vacating"
          ? "борт сходит с полосы — подождите съезд"
          : !occ.dead &&
              (occ.status === "landing" ||
                occ.status === "inbound" ||
                occ.status === "takeoff")
            ? `на полосе ${occ.cs}`
            : `занята бортом ${occ.cs || ""}`.trim();
      hooks.toast(`${r.name}: ${why}`, "bad");
      return;
    }
  }
  if (r.clearSnowT > 0) {
    hooks.toast(`${r.name}: уборка уже идет`);
    return;
  }
  if (g.clearLockT > 0) {
    hooks.toast(
      `Уборка временно недоступна · ${Math.ceil(g.clearLockT)}с`,
      "bad",
    );
    return;
  }
  if (r.snow < 8) {
    hooks.toast(`${r.name} и так почти чистая`);
    return;
  }
  const other = g.runways[1 - ri];
  if (other && other.clearSnowT > 0) {
    hooks.toast("Сейчас чистится другая полоса — дождитесь", "bad");
    return;
  }

  const dur = clamp(5.5 + r.snow * 0.04, 5.5, 10) / g.cfg.plow;
  r.closed = true;
  r.clearSnowT = dur;
  r.clearSnowMax = dur;
  r._snowStart = r.snow;
  r.clearX = r.x1 + 36;
  hooks.toast(`${r.name}: уборка снега · ~${Math.ceil(dur)}с`);
  Snd.ok();
  if (g.tutStep === 3) hooks.tutNext();
  if (other?.closed) {
    addScore(g, hooks, -60, null, "ОБЕ ПОЛОСЫ ЗАКРЫТЫ");
    hooks.toast("Обе полосы закрыты одновременно!", "bad");
    g.worstMistake = "Одновременное закрытие обеих полос";
  }
}

/** Нанести реагент на полосу. */
export function cmdTreatIce(
  g: GameState,
  hooks: GameHooks,
  ri: number,
): void {
  const r = g.runways[ri];
  if (!r) return;
  if ((r.ice || 0) < 6) {
    hooks.toast(
      `${r.name}: гололеда почти нет (полоска льда пустая) — реагент не нужен`,
    );
    return;
  }
  if (r.clearIceT > 0) {
    hooks.toast(`${r.name}: реагент уже наносится`);
    return;
  }
  const need = 12;
  if (g.reagent < need) {
    hooks.toast(
      `Мало реагента на складе (нужно ${need}%, есть ${Math.round(g.reagent)}%) — ждите событие поставки или экономьте`,
      "bad",
    );
    return;
  }
  g.reagent -= need;
  g.reagentUsed += need;
  r.clearIceT = 2.2;
  r.clearIceMax = 2.2;
  r._iceStart = r.ice || 0;
  hooks.toast(
    `${r.name}: реагент на полосе (−${need}% склада) — гололед спадет за ~2с`,
    "good",
  );
  Snd.ok();
  for (let i = 0; i < 10; i++) {
    g.sparks.push({
      x: rnd(r.x1 + 40, r.x2 - 40),
      y: r.y + rnd(-12, 12),
      vx: rnd(-20, 20),
      vy: rnd(-30, -8),
      t: rnd(0.3, 0.7),
      c: "rgba(150,220,255,.85)",
      s: rnd(1.2, 2.4),
    });
  }
}

/** Сжать таймеры активных угроз. */
export function cmdResolveHazard(g: GameState, hooks: GameHooks): void {
  if (!g.hazards.length) {
    hooks.toast("Сейчас угроз нет");
    return;
  }
  for (const h of g.hazards) h.t = Math.min(h.t, 0.35);
  g.flash = 0.25;
  hooks.toast("Угроза снята", "good");
  addScore(g, hooks, 20, null, "УГРОЗА СНЯТА");
  Snd.ok();
}
